import { computed, inject, Injectable, signal } from "@angular/core";
import { EventUpload, UploadedMediaItem } from "../_models/event-upload.model";
import { StreappEventsService } from "src/app/services/streapp-events.service";
import { AuthService } from "src/app/services/auth.service";
import { UploadService } from "./upload.service";
import { EventType, EventTypeMain } from "../_models/eventtype.model";

@Injectable()
export class EventFormStore {
    
    public streappService = inject(StreappEventsService);

    public authService = inject(AuthService);

    public uploadService = inject(UploadService);

    public readonly isLoading = signal(false);

    public readonly isPublishing = signal(false);

    public readonly error = signal<string | null>(null);

    public streappEvent = signal<EventUpload>({
        id: '',
        title: '',
        description: '',
        category: undefined,
        subcategory: undefined,
        privacyEnabled: true,
        injuriesPresent: undefined,
        emergencyServicesPresent: undefined,
        highImpactEvent: undefined,
        tags: [],
        location: '',
        latitude: 0,
        longtitude: 0,
        linkedToMainStreappID: undefined,
        created: new Date(),
        mediaItems: [],
        preUploadedMediaItems: [],
        preUploadedShopMediaItems: []
    });

    public readonly isHighImpactEvent = computed(() => {
        return this.streappService.eventTypes().find(e => e === this.streappEvent().category)!.highImpact;
    });

    public privacyStepLeaveGuard: (() => Promise<boolean>) | null = null;

    public registerPrivacyStepLeaveGuard(guard: (() => Promise<boolean>) | null): void {
        this.privacyStepLeaveGuard = guard;
    }
    
    public automaticBlur = signal(true);

    public currentStep = signal(0);

    public readonly canGoBack = computed(() => 
        this.currentStep() > 0 && !this.isLoading() && !this.isPublishing()
    );

    public stepMediaItemsUploadValid = signal(false);
    public stepLocationValid = signal(false);
    public stepDetailsValid = signal(false);
    public stepCategoryValid = signal(false);
    public stepPrivacyValid = signal(false);
    public stepPriceValid = signal(false);

    public eventID = signal<string | undefined>(undefined);

    public preUploadedMediaItems = computed(() => this.streappEvent().preUploadedMediaItems);

    public preUploadedShopMediaItems = computed(() => this.streappEvent().preUploadedShopMediaItems);

    public uploadItems = computed(() => [...this.preUploadedMediaItems(), ...this.preUploadedShopMediaItems()]);

    public progress = computed(() => {
        const totalItems = this.uploadItems().length;
        const finishedItems = this.uploadItems().filter(item => item.finished).length;
        return `${finishedItems} van de ${totalItems} voltooid`;
    });

    public addMediaItems(mediaItem: UploadedMediaItem, uploadBucket: string): void {
        if (uploadBucket === 'shop') {
            this.streappEvent.update(streapp => ({
                ...streapp,
                preUploadedShopMediaItems: [...streapp.preUploadedShopMediaItems, mediaItem]
            }));
        } else {
            this.streappEvent.update(streapp => ({
                ...streapp,
                preUploadedMediaItems: [...streapp.preUploadedMediaItems, mediaItem]
            }));
        }
    }

    public removeMediaItem(index: number, uploadBucket: string): void {
        let updatedMediaItems: Array<UploadedMediaItem>;
        if (uploadBucket === 'shop') {
            updatedMediaItems = this.streappEvent().preUploadedShopMediaItems.filter((_, i) => i !== index);
            this.streappEvent.update(streapp => ({
                ...streapp,
                preUploadedShopMediaItems: updatedMediaItems
            }));
        } else {
            updatedMediaItems = this.streappEvent().preUploadedMediaItems.filter((_, i) => i !== index);
            this.streappEvent.update(streapp => ({
                ...streapp,
                preUploadedMediaItems: updatedMediaItems
            }));
        }
    }

    public updateLocation(lat: number, long: number, location: string): void {
        this.streappEvent.update(streapp => ({
            ...streapp,
            latitude: lat,
            longtitude: long,
            location: location
        }));
    }

    public setDetailsFormValue(value: any): void {
        this.streappEvent.update(streapp => ({
            ...streapp,
            title: value.titel,
            description: value.beschrijving,
            created: value.created,
        }));
    }

    public setAdditionalDetailsFormValue(value: any): void {
        this.streappEvent.update(streapp => ({
            ...streapp,
            privacyEnabled: value.privacyEnabled,
            emergencyServicesPresent: value.emergencyServicesPresent,
            highImpactEvent: value.highImpactEvent
        }));
    }

    public updateCategories(category: EventTypeMain, subcategory: EventType): void {
        this.streappEvent.update(streapp => ({
            ...streapp,
            category: category,
            subcategory: subcategory
        }));
    }

    public updatePriceForMediaItem(index: number, price: string, uploadBucket: string): void {
        if (uploadBucket === 'shop') {
            this.streappEvent.update(streapp => {
                const updatedShopMediaItems = streapp.preUploadedShopMediaItems.map((item, i) => {
                    if (i === index) {
                        return {
                            ...item,
                            price: price,
                            priceChanged: true
                        };
                    }
                    return item;
                });
                return {
                    ...streapp,
                    preUploadedShopMediaItems: updatedShopMediaItems
                };
            });
        } else {
            this.streappEvent.update(streapp => {
                const updatedMediaItems = streapp.preUploadedMediaItems.map((item, i) => {
                    if (i === index) {
                        return {
                            ...item,
                            price: price,
                            priceChanged: true
                        };
                    }
                    return item;
                });
                return {
                    ...streapp,
                    preUploadedMediaItems: updatedMediaItems
                };
            });
        }
    }

    public resetEvent(): void {
        this.streappEvent.set({
            id: '',
            title: '',
            description: '',
            category: undefined,
            subcategory: undefined,
            privacyEnabled: true,
            injuriesPresent: '',
            emergencyServicesPresent: false,
            highImpactEvent: false,
            tags: [],
            location: '',
            latitude: 0,
            longtitude: 0,
            linkedToMainStreappID: undefined,
            created: new Date(),
            mediaItems: [],
            preUploadedMediaItems: [],
            preUploadedShopMediaItems: []
        });
    }

    updateProgress(index: number, progress: number, finished: boolean, shop: boolean): void {
        if (shop) {
            this.streappEvent.update(streapp => ({
                ...streapp,
                preUploadedShopMediaItems: streapp.preUploadedShopMediaItems.map((item, i) => {
                    if (i === index) {
                        return {
                            ...item,
                            progress: progress,
                            finished: finished,
                            hasError: item.hasError
                        };
                    }
                    return item;
                })
            }));
        } else {
            this.streappEvent.update(streapp => ({
                ...streapp,
                preUploadedMediaItems: streapp.preUploadedMediaItems.map((item, i) => {
                    if (i === index) {
                        return {
                            ...item,
                            progress: progress,
                            finished: finished,
                            hasError: item.hasError
                        };
                    }
                    return item;
                })
            }));
        }
    }

    public getErrorMessage(error: any): string {
        if (error?.message) {
            return error.message;
        }
        return 'Er is iets misgegaan';
    }
}